import type { Mask } from "@/core/types"
import { clamp, lerp } from "@/utils/math"
import type { vec2 } from "gl-matrix"

export interface MaskIconOverlay {
	groupId: string
	id: string
	type: Mask["type"]
	selected: boolean
	x: number
	y: number
}

export function getMaskIconOverlay(
	mask: Mask,
	groupId: string,
	maskDetectionCoords: Readonly<Record<string, vec2>> = {},
	selected = false,
): MaskIconOverlay | undefined {
	switch (mask.type) {
		case "background":
			return {
				groupId,
				id: mask.id,
				type: mask.type,
				selected,
				x: 0.5,
				y: 0.4,
			}

		case "subject": {
			const coords = maskDetectionCoords[mask.id]
			return {
				groupId,
				id: mask.id,
				type: mask.type,
				selected,
				x: coords?.[0] ?? 0.5,
				y: coords?.[1] ?? 0.5,
			}
		}

		case "people": {
			const coords = maskDetectionCoords[mask.id]
			if (!coords) {
				return
			}
			return {
				groupId,
				id: mask.id,
				type: mask.type,
				selected,
				x: coords[0],
				y: coords[1],
			}
		}

		case "sky":
			return {
				groupId,
				id: mask.id,
				type: mask.type,
				selected,
				x: 0.5,
				y: 0.2,
			}

		case "radial":
			return {
				groupId,
				id: mask.id,
				type: mask.type,
				selected,
				x: mask.centerX,
				y: mask.centerY,
			}

		case "gradient":
			return {
				groupId,
				id: mask.id,
				type: mask.type,
				selected,
				x: lerp(mask.startX, mask.endX, 0.5),
				y: lerp(mask.startY, mask.endY, 0.5),
			}

		case "brush": {
			const firstStrokePoints = mask.strokes[0]?.points
			const firstStrokeX = firstStrokePoints?.at(0) ?? 0.5
			const firstStrokeY = firstStrokePoints?.at(1) ?? 0.5

			return {
				groupId,
				id: mask.id,
				type: mask.type,
				selected,
				x: firstStrokeX,
				y: firstStrokeY,
			}
		}
	}
}

export function spreadOverlappingMaskIconOverlays(pins: MaskIconOverlay[]) {
	const collisionGroups = new Map<string, MaskIconOverlay[]>()
	for (const pin of pins) {
		const key = `${pin.x.toFixed(4)}:${pin.y.toFixed(4)}`
		collisionGroups.set(key, [...(collisionGroups.get(key) ?? []), pin])
	}

	return pins.map((pin) => {
		const key = `${pin.x.toFixed(4)}:${pin.y.toFixed(4)}`
		const group = collisionGroups.get(key)
		if (!group || group.length <= 1) {
			return pin
		}

		const index = group.findIndex((item) => item.id === pin.id)
		const angle = (Math.PI * 2 * index) / group.length - Math.PI / 2
		const radius = 0.018
		return {
			...pin,
			x: clamp(pin.x + Math.cos(angle) * radius, 0, 1),
			y: clamp(pin.y + Math.sin(angle) * radius, 0, 1),
		}
	})
}
